import { useEffect, useState } from "react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { getDealerPageDetails, updateDealerPageContent } from "../api/api";
import { toast } from "sonner";
import { AdminLoader } from "../components/common/AdminLoader";
import {
  FiChevronDown,
  FiChevronUp,
  FiSave,
  FiClock,
  FiFileText,
  FiAlertCircle,
  FiCheckCircle,
  FiSend,
  FiInfo,
} from "react-icons/fi";




const textareaClass =
  "w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:bg-white focus:border-[#B08D55] focus:ring-4 focus:ring-[#B08D55]/10 transition-all outline-none text-gray-700 resize-none";
const labelClass =
  "text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 block";
const cardClass =
  "bg-white shadow-sm rounded-xl overflow-hidden border border-gray-100";

const DealerEditPage = () => {

  const [savingContent, setSavingContent] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({
    content: false,
    basic: false,
    social: false,
    hours: false,
    services: false,
    card: false,
    images: false,
  });

  // Body content & edit quota state
  const [content, setContent] = useState("");
  const [initialContent, setInitialContent] = useState("");
  const [contentEditCount, setContentEditCount] = useState(0);
  const [maxContentEdits, setMaxContentEdits] = useState(2);
  const [latestRequest, setLatestRequest] = useState<any>(null);
  const [requestReason, setRequestReason] = useState("");
  const [showReasonInput, setShowReasonInput] = useState(false);



  const fetchData = async () => {
    try {
      const { data } = await getDealerPageDetails();
      if (data.success && data.data) {

        const page = data.data.page;
        const fetchedContent = data.data.content || "";

        setContent(fetchedContent);
        setInitialContent(fetchedContent);
        setContentEditCount(page?.contentEditCount ?? 0);
        setMaxContentEdits(page?.maxContentEdits ?? 2);
        setLatestRequest(data.data.requests?.latestContentEditRequest || null);


      }
    } catch {
      // page not yet created
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const toggle = (key: string) =>
    setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));





  const remainingEdits = Math.max(0, maxContentEdits - contentEditCount);
  const isContentChanged = content !== initialContent;

  const handleSaveContent = async () => {
    if (!isContentChanged) {
      toast.info("No content changes to save");
      return;
    }

    if (remainingEdits <= 0 && !showReasonInput) {
      setShowReasonInput(true);
      return;
    }

    setSavingContent(true);
    try {
      const { data } = await updateDealerPageContent(content, requestReason);
      if (data.success) {
        if (data.data?.direct) {
          toast.success(data.message || "Page body content updated successfully!");
          setContentEditCount(data.data.contentEditCount);
          setInitialContent(content);
        } else {
          toast.success(data.message || "Content edit request submitted for approval!");
          setLatestRequest(data.data.request);
          setShowReasonInput(false);
          setRequestReason("");
        }
        await fetchData();
      } else {
        toast.error(data.message || "Failed to update content");
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Failed to save body content");
    } finally {
      setSavingContent(false);
    }
  };

  if (fetching) {
    return <AdminLoader fullScreen={false} size="md" message="Loading page editor..." className="min-h-[16rem]" />;
  }

  const renderSection = (
    key: string,
    icon: React.ReactNode,
    title: string,
    description: string,
    children: React.ReactNode
  ) => (
    <div className={cardClass}>
      <div
        className="p-6 border-b border-gray-100 flex justify-between items-center cursor-pointer hover:bg-gray-50/50 transition"
        onClick={() => toggle(key)}
      >
        <div>
          <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
            <span className="text-[#B08D55]">{icon}</span>
            {title}
          </h3>
          <p className="text-sm text-gray-500 mt-1">{description}</p>
        </div>
        <div className="p-1 text-gray-400">
          {collapsed[key] ? (
            <FiChevronDown size={20} />
          ) : (
            <FiChevronUp size={20} />
          )}
        </div>
      </div>
      {!collapsed[key] && (
        <div className="p-6 space-y-5 animate-in slide-in-from-top-2 duration-200">
          {children}
        </div>
      )}
    </div>
  );



  return (
    <div className="font-poppins w-full">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <h1 className="text-4xl font-black text-gray-900 tracking-tight">
            Edit Shopify Page
          </h1>
          <p className="text-gray-500 mt-1">
            Review and sync your profile data to your Shopify dealer page.
          </p>
        </div>
      </div>

      {/* Pending Request Alert */}
      {latestRequest?.status === "PENDING" && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3 shadow-sm">
          <FiClock className="text-amber-600 mt-0.5 shrink-0" size={20} />
          <div className="flex-1">
            <h4 className="text-sm font-bold text-amber-900">Content Update Request Pending</h4>
            <p className="text-xs text-amber-700 mt-0.5">
              You submitted a body content update on{" "}
              {new Date(latestRequest.createdAt).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
              })}
              . Our admin/content manager team is reviewing your changes.
            </p>
            {latestRequest.reason && (
              <p className="text-xs text-amber-800 italic mt-1 bg-amber-100/60 px-2.5 py-1 rounded inline-block">
                Reason: "{latestRequest.reason}"
              </p>
            )}
          </div>
        </div>
      )}

      {/* Rejected Request Alert */}
      {latestRequest?.status === "REJECTED" && latestRequest.rejectionReason && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3 shadow-sm">
          <FiAlertCircle className="text-red-600 mt-0.5 shrink-0" size={20} />
          <div className="flex-1">
            <h4 className="text-sm font-bold text-red-900">Previous Content Request Rejected</h4>
            <p className="text-xs text-red-700 mt-0.5">
              Reason: {latestRequest.rejectionReason}
            </p>
          </div>
        </div>
      )}

      <div className="space-y-6">
        {/* Page Body Content Section */}
        {renderSection(
          "content",
          <FiFileText size={20} />,
          "Page Body Content",
          "Rich text article & description displayed on your public dealer page",
          <div className="space-y-4">
            <div className="flex items-center justify-between pb-2 border-b border-gray-100 flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-gray-600">Edit Quota Status:</span>
                {remainingEdits > 0 ? (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                    <FiCheckCircle size={12} />
                    {remainingEdits} of {maxContentEdits} direct edits remaining
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-800 border border-amber-200">
                    <FiAlertCircle size={12} />
                    {contentEditCount} of {maxContentEdits} edits used (Approval required)
                  </span>
                )}
              </div>

              <div className="text-xs text-gray-500">
                {isContentChanged ? (
                  <span className="text-amber-600 font-medium">Unsaved content changes</span>
                ) : (
                  <span>Content up to date</span>
                )}
              </div>
            </div>

            {remainingEdits <= 0 && (
              <div className="p-3 bg-amber-50/80 border border-amber-200/80 rounded-lg text-xs text-amber-800 flex items-start gap-2">
                <FiInfo className="text-amber-600 shrink-0 mt-0.5" size={14} />
                <div>
                  <span className="font-semibold">Direct edit limit reached:</span> You have already used your {maxContentEdits} free edits. You can still modify the content below and submit a request. An admin or content manager will review and approve your update.
                </div>
              </div>
            )}

            <div>
              <label className={labelClass}>Body HTML Content</label>
              <div className="rounded-lg overflow-hidden border border-gray-200 focus-within:border-[#B08D55] transition-colors">
                <ReactQuill
                  theme="snow"
                  value={content}
                  onChange={(val) => setContent(val)}
                  placeholder="Write your custom dealer story, installation services, and store highlights here..."
                  className="bg-white min-h-[220px]"
                />
              </div>
            </div>

            {remainingEdits <= 0 && (
              <div>
                <label className={labelClass}>Reason for Update (Optional for Reviewers)</label>
                <textarea
                  className={`${textareaClass} h-20 text-xs`}
                  placeholder="Explain what was changed and why (e.g. Updated showroom equipment list, store relocation notice...)"
                  value={requestReason}
                  onChange={(e) => setRequestReason(e.target.value)}
                />
              </div>
            )}

            <div className="flex justify-end pt-2">
              <button
                type="button"
                onClick={handleSaveContent}
                disabled={savingContent || !isContentChanged || (remainingEdits <= 0 && latestRequest?.status === "PENDING")}
                className={`flex items-center gap-2 px-5 py-2 text-sm font-semibold rounded-lg shadow-sm transition disabled:opacity-50 ${remainingEdits > 0
                  ? "bg-[#B08D55] hover:bg-[#967645] text-white"
                  : "bg-gray-900 hover:bg-gray-800 text-white"
                  }`}
              >
                {remainingEdits > 0 ? (
                  <>
                    <FiSave size={16} />
                    {savingContent ? "Saving Content..." : `Save Body Content (${remainingEdits} edit${remainingEdits > 1 ? 's' : ''} left)`}
                  </>
                ) : (
                  <>
                    <FiSend size={16} />
                    {savingContent
                      ? "Submitting Request..."
                      : latestRequest?.status === "PENDING"
                        ? "Pending Review (Resubmit Changes)"
                        : "Submit Request to Admin"}
                  </>
                )}
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default DealerEditPage;
